export type NutrientTrendDirection = "low" | "high";
export type NutrientTrendWindowKey = "10d" | "90d" | "180d";

export interface NutrientTrendAlert {
  nutrientId: string;
  direction: NutrientTrendDirection;
  windows: NutrientTrendWindowKey[];
  ruleSummary: string;
  severity: "soft" | "moderate" | "high";
}

export type DailyPercentDvByNutrient = Record<string, Record<string, number>>;

interface TrendWindowRuleConfig {
  key: NutrientTrendWindowKey;
  days: number;
  minPointsRequired: number;
}

interface NutrientTrendAnalyzerConfig {
  lowThreshold: number;
  veryLowThreshold: number;
  highThreshold: number;
  veryHighThreshold: number;
  ninetyDayHighAverageThreshold: number;
  oneHundredEightyDayHighAverageThreshold: number;
  windows: TrendWindowRuleConfig[];
}

const DEFAULT_CONFIGURATION: NutrientTrendAnalyzerConfig = {
  lowThreshold: 70,
  veryLowThreshold: 50,
  highThreshold: 200,
  veryHighThreshold: 300,
  ninetyDayHighAverageThreshold: 180,
  oneHundredEightyDayHighAverageThreshold: 160,
  windows: [
    { key: "10d", days: 10, minPointsRequired: 7 },
    { key: "90d", days: 90, minPointsRequired: 45 },
    { key: "180d", days: 180, minPointsRequired: 60 },
  ],
};

export class NutrientTrendAnalyzer {
  private readonly dailyPercentDvByNutrient: DailyPercentDvByNutrient;
  private readonly configuration: NutrientTrendAnalyzerConfig;

  constructor(
    dailyPercentDvByNutrient: DailyPercentDvByNutrient | null | undefined,
    configurationOverrides?: Partial<NutrientTrendAnalyzerConfig>,
  ) {
    this.dailyPercentDvByNutrient = dailyPercentDvByNutrient || {};
    this.configuration = {
      ...DEFAULT_CONFIGURATION,
      ...(configurationOverrides || {}),
      windows: configurationOverrides?.windows ?? DEFAULT_CONFIGURATION.windows,
    };
  }

  public analyze(): NutrientTrendAlert[] {
    const alerts: NutrientTrendAlert[] = [];

    const nutrientIds = Object.keys(this.dailyPercentDvByNutrient);

    nutrientIds.forEach((nutrientId) => {
      const series = this.dailyPercentDvByNutrient[nutrientId] || {};
      const dates = Object.keys(series).sort(); // ISO YYYY-MM-DD => lexicographic = chronological

      if (dates.length === 0) {
        return;
      }

      const lowWindows: NutrientTrendWindowKey[] = [];
      const highWindows: NutrientTrendWindowKey[] = [];
      const lowRuleSummaries: string[] = [];
      const highRuleSummaries: string[] = [];

      let lowHasChronicFlag = false;
      let highHasChronicFlag = false;

      this.configuration.windows.forEach((windowConfig) => {
        const { key, days, minPointsRequired } = windowConfig;

        const sampleDates =
          dates.length > days ? dates.slice(dates.length - days) : [...dates];

        if (sampleDates.length < minPointsRequired) {
          return;
        }

        let consideredDays = 0;
        let sum = 0;

        let lowCount = 0;
        let veryLowCount = 0;
        let highCount = 0;
        let veryHighCount = 0;

        sampleDates.forEach((isoDate) => {
          const raw = series[isoDate];
          const value = Number(raw);

          if (!Number.isFinite(value)) {
            return;
          }

          consideredDays += 1;
          sum += value;

          if (value < this.configuration.lowThreshold) {
            lowCount += 1;
            if (value < this.configuration.veryLowThreshold) {
              veryLowCount += 1;
            }
          } else if (value > this.configuration.highThreshold) {
            highCount += 1;
            if (value > this.configuration.veryHighThreshold) {
              veryHighCount += 1;
            }
          }
        });

        if (consideredDays < minPointsRequired) {
          return;
        }

        const average = consideredDays > 0 ? sum / consideredDays : 0;

        if (key === "10d") {
          if (lowCount >= 7) {
            lowWindows.push("10d");
            lowRuleSummaries.push(
              `At least ${lowCount} of the last ${consideredDays} days were below ${this.configuration.lowThreshold}% DV`,
            );
          }

          if (highCount >= 7) {
            highWindows.push("10d");
            highRuleSummaries.push(
              `At least ${highCount} of the last ${consideredDays} days were above ${this.configuration.highThreshold}% DV`,
            );
          }
        } else if (key === "90d") {
          if (average < this.configuration.lowThreshold && lowCount >= 45) {
            lowWindows.push("90d");
            lowRuleSummaries.push(
              `Over approximately 3 months, the average intake was below ${this.configuration.lowThreshold}% DV with ${lowCount} low days`,
            );
          }

          if (average > this.configuration.ninetyDayHighAverageThreshold) {
            highWindows.push("90d");
            highRuleSummaries.push(
              `Over approximately 3 months, the average intake was above ${this.configuration.ninetyDayHighAverageThreshold}% DV`,
            );
          }
        } else if (key === "180d") {
          if (average < this.configuration.lowThreshold && lowCount >= 90) {
            lowWindows.push("180d");
            lowHasChronicFlag = true;
            lowRuleSummaries.push(
              `Over approximately 6 months, intake has been chronically low (average below ${this.configuration.lowThreshold}% DV with ${lowCount} low days)`,
            );
          }

          if (
            average >
              this.configuration.oneHundredEightyDayHighAverageThreshold &&
            highCount >= 90
          ) {
            highWindows.push("180d");
            highHasChronicFlag = true;
            highRuleSummaries.push(
              `Over approximately 6 months, intake has been chronically high (average above ${this.configuration.oneHundredEightyDayHighAverageThreshold}% DV with ${highCount} high days)`,
            );
          }
        }

        if (!lowHasChronicFlag && veryLowCount > 0) {
          lowHasChronicFlag = key !== "10d";
        }

        if (!highHasChronicFlag && veryHighCount > 0) {
          highHasChronicFlag = key !== "10d";
        }
      });

      if (lowWindows.length > 0) {
        alerts.push({
          nutrientId,
          direction: "low",
          windows: Array.from(new Set(lowWindows)) as NutrientTrendWindowKey[],
          ruleSummary: joinRuleSummaries(lowRuleSummaries),
          severity: deriveSeverity(lowWindows, lowHasChronicFlag),
        });
      }

      if (highWindows.length > 0) {
        alerts.push({
          nutrientId,
          direction: "high",
          windows: Array.from(new Set(highWindows)) as NutrientTrendWindowKey[],
          ruleSummary: joinRuleSummaries(highRuleSummaries),
          severity: deriveSeverity(highWindows, highHasChronicFlag),
        });
      }
    });

    return alerts;
  }
}

function joinRuleSummaries(summaries: string[]): string {
  if (!summaries || summaries.length === 0) {
    return "";
  }
  return summaries.join(" | ");
}

function deriveSeverity(
  windows: NutrientTrendWindowKey[],
  hasChronicFlag: boolean,
): "soft" | "moderate" | "high" {
  if (!windows || windows.length === 0) {
    return "soft";
  }

  if (hasChronicFlag || windows.includes("180d")) {
    return "high";
  }

  if (windows.includes("90d")) {
    return "moderate";
  }

  return "soft";
}
